import { ReactElement, ReactNode } from "react";
import Button from "../../Element/Button";
import ResponsiveImage from "../../Element/ResponsiveImage";
import { ImageCrop } from "../../Utility/dataTypes";
import styles from "./index.module.css";
import cx from "classnames";
import { onClickMaybeScrollToHref } from "../../../utils/scroll";
import { ConsIcon, ProsIcon } from "./utils";

type Props = {
  id: string;
  name: string;
  header: string;
  image?: ImageCrop;
  discount?: string;
  dealsSource: {
    name: string;
    url: string;
    original: string;
    discount: string;
    discountPrice: string;
  }[];
  pros?: ReactNode[];
  cons?: ReactNode[];
  detailed?: boolean;
  hidePriceInButton?: boolean;
  showBestPrice?: boolean;
};

export default function ProductContent({
  id,
  name,
  header,
  image,
  dealsSource,
  detailed = false,
  pros,
  cons,
  discount,
  hidePriceInButton,
  showBestPrice,
}: Props): ReactElement {
  return (
    <div className={cx(styles.container, detailed && styles.detailed)}>
      <a
        id={detailed ? `product-${id}` : undefined}
        href={detailed ? undefined : `#product-${id}`}
        onClick={detailed ? undefined : onClickMaybeScrollToHref}
        className={cx(styles.image, detailed && styles.detailed)}
      >
        {image && (
          <ResponsiveImage
            {...image}
            fill="contain"
            fillPosition="center top"
            fillAspectRatio={1}
            displayWidth={detailed ? 270 : 165}
          />
        )}
      </a>

      <div className={styles.content}>
        {detailed ? (
          <>
            <div className={styles.header}>{header}</div>
            <div className={styles.name}>{name}</div>
          </>
        ) : (
          <a href={`#product-${id}`} onClick={onClickMaybeScrollToHref}>
            <div className={styles.header}>{header}</div>
            <div className={styles.name}>{name}</div>
            <div className={styles.readmore}>Read more</div>
          </a>
        )}
      </div>

      {detailed && (
        <div className={styles.price}>
          {showBestPrice ? (
            <>
              <p className={styles.savings}>{dealsSource[0]?.discountPrice}</p>
              <div className={styles.discountblock}>
                <p className={styles.original}>{dealsSource[0]?.original}</p>
                <p className={styles.discount}>
                  {dealsSource[0]?.discount &&
                    `${dealsSource[0]?.discount} OFF`}
                </p>
              </div>
            </>
          ) : (
            discount && <div className={styles.discountLabel}>{discount}</div>
          )}
        </div>
      )}

      <div className={styles.deals}>
        {dealsSource.map((deal, index) => (
          <Button
            variant="secondary"
            key={index}
            href={deal.url}
            rel="nofollow"
            target="_blank"
          >
            {!hidePriceInButton && `${deal.discountPrice || "Shop"} at `}
            {deal.name}
          </Button>
        ))}
      </div>

      {image?.credit && detailed && (
        <div className={styles.credit}>{image.credit}</div>
      )}

      {detailed && (
        <div className={styles.reviews}>
          {pros && pros.length !== 0 && (
            <div className={styles.pros}>
              <p>
                <span>{ProsIcon}</span> PROS
              </p>
              <ul>
                {pros.map((text, i) => (
                  <li key={i}>{text}</li>
                ))}
              </ul>
            </div>
          )}
          {cons && cons.length !== 0 && (
            <div className={styles.cons}>
              <p>
                <span>{ConsIcon}</span> CONS
              </p>
              <ul>
                {cons.map((text, i) => (
                  <li key={i}>{text}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
